import React from 'react';
import {motion} from 'framer-motion';
import {
    checkNull,
    checkNullJson,
    checkNullStr,
    elementHasChildren,
    getDefStr,
    isHtmlElement,
    isReactElement,
    parseInteger,
    printError,
    printLog
} from "../utils/ReactUtils";

/**
 * Animation-types for the {@link FramerAnimator} to determine the style of animation that is used to render elements.
 * @readonly
 * @enum {string}
 */
const AnimationType = {
    SLIDE_DOWN: 'slideDown',
    SLIDE_UP: 'slideUp',
    SLIDE_LEFT: 'slideLeft',
    SLIDE_RIGHT: 'slideRight',
    FADE_IN: 'fadeIn',
    FADE_OUT: 'fadeOut',
    SCALE_UP: 'scaleUp',
    SCALE_DOWN: 'scaleDown',
    ROTATE: 'rotate',
    ROTATE_IN: 'rotateIn',
    ROTATE_OUT: 'rotateOut',
    BOUNCE: 'bounce',
    BOUNCE_IN: 'bounceIn',
    BOUNCE_OUT: 'bounceOut',
    FLIP: 'flip',
    FLIP_IN: 'flipIn',
    FLIP_OUT: 'flipOut',
    ZOOM_IN: 'zoomIn',
    ZOOM_OUT: 'zoomOut',
    FLASH: 'flash',
    PULSE: 'pulse',
    RUBBER_BAND: 'rubberBand',
    SHAKE: 'shake',
    SWING: 'swing',
    TADA: 'tada',
    WOBBLE: 'wobble',
    JELLO: 'jello',
    HEART_BEAT: 'heartBeat',
};

/**
 * Animation easing-types for the {@link FramerAnimator} that works like interpolaters
 * that determines the flow & control of the animation while rendering elements in the DOM.
 * @readonly
 * @enum {string}
 */
const EasingType = {
    LINEAR: 'linear',
    EASE_IN: 'easeIn',
    EASE_OUT: 'easeOut',
    EASE_IN_OUT: 'easeInOut',
    EASE_IN_BACK: 'easeInBack',
    EASE_OUT_BACK: 'easeOutBack',
    EASE_IN_OUT_BACK: 'easeInOutBack',
    EASE_IN_CIRC: 'easeInCirc',
    EASE_OUT_CIRC: 'easeOutCirc',
    EASE_IN_OUT_CIRC: 'easeInOutCirc',
    EASE_IN_CUBIC: 'easeInCubic',
    EASE_OUT_CUBIC: 'easeOutCubic',
    EASE_IN_OUT_CUBIC: 'easeInOutCubic',
    EASE_IN_ELASTIC: 'easeInElastic',
    EASE_OUT_ELASTIC: 'easeOutElastic',
    EASE_IN_OUT_ELASTIC: 'easeInOutElastic',
    EASE_IN_EXPO: 'easeInExpo',
    EASE_OUT_EXPO: 'easeOutExpo',
    EASE_IN_OUT_EXPO: 'easeInOutExpo',
    EASE_IN_QUAD: 'easeInQuad',
    EASE_OUT_QUAD: 'easeOutQuad',
    EASE_IN_OUT_QUAD: 'easeInOutQuad',
    EASE_IN_QUART: 'easeInQuart',
    EASE_OUT_QUART: 'easeOutQuart',
    EASE_IN_OUT_QUART: 'easeInOutQuart',
    EASE_IN_QUINT: 'easeInQuint',
    EASE_OUT_QUINT: 'easeOutQuint',
    EASE_IN_OUT_QUINT: 'easeInOutQuint',
    EASE_IN_SINE: 'easeInSine',
    EASE_OUT_SINE: 'easeOutSine',
    EASE_IN_OUT_SINE: 'easeInOutSine',
};


/**
 * Class representing a Framer Animator.
 *
 * @example
 * const animator = new FramerAnimator();
 *  animator
 *  .setElementId('myElement')
 *  .setAnimationType(AnimationType.SLIDE_DOWN)
 *  .setDuration(500)
 *  .setInitialHide(true)
 *  .setEasing('easeInOut')
 *  .setLoop(false)
 *  .setDelay(100)
 *  .setSpringConfig({ stiffness: 100, damping: 20 });
 *
 *
 * // Render the element() you want to animate in the render method inside the component
 * const MyComponent = () => {
 *     return (<>
 *         //Animate single element:
 *         {animator.render(
 *             <div>This is animated component</div>
 *         )}
 *
 *         //Animate children of the element with progressive delays:
 *         {animator.renderChildren(
 *             <div>
 *                 <div>This is animated component 1</div>
 *                 <div>This is animated component 2</div>
 *                 <div>This is animated component 3</div>
 *             </div>
 *         )}
 *     </div>)
 * };
 */
class FramerAnimator {
    TAG = 'FramerAnimator';


    /**
     * Create a Framer Animator.
     *
     * For example, check: {@link FramerAnimator}
     */
    constructor() {
        this.elementId = '';
        this.elementRef = '';
        this.elementClassName = '';
        this.animationType = AnimationType.FADE_IN;
        this.duration = 400;
        this.initialAnimPercent = 50;
        this.initialHide = true;
        this.easing = EasingType.EASE_IN_OUT;
        this.loop = false;
        this.delay = 200;
        this.repeatCount = 0;
        this.springConfig = {stiffness: 100, damping: 10};
        this.transition = {};

        this.setDefaults();
    }

    /**
     * Set default values for the animator.
     * @returns {void}
     */
    setDefaults() {
        this.transition = {
            duration: this.duration / 1000, ease: this.easing, delay: this.delay / 1000,
        };
    }

    /**
     * Set the element ID.
     * @param {string} id - The ID of the element to animate.
     * @returns {FramerAnimator}
     */
    setElementId(id) {
        this.elementId = id;
        return this;
    }

    /**
     * Set the element className.
     * @param {string} name - The className of the element to animate.
     * @returns {FramerAnimator}
     */
    setElementClassName(name) {
        this.elementClassName = name;
        return this;
    }

    /**
     * Set the element-ref.
     * @param {useRef} ref - The ref of the element to animate.
     * @returns {FramerAnimator}
     */
    setElementRef(ref) {
        this.elementRef = ref;
        return this;
    }

    /**
     * Set the animation type. Use {@link AnimationType}.
     * @param {string} type - The type of animation. Default is {@link AnimationType}.FADE_IN.
     * @returns {FramerAnimator}
     */
    setAnimationType(type) {
        this.animationType = type;
        return this;
    }

    /**
     * Set the easing function. Use {@link EasingType}.
     * @param {string} easing - The easing function. Default is {@link EasingType}.EASE_IN_OUT.
     * @returns {FramerAnimator}
     */
    setEasing(easing) {
        this.easing = easing;
        this.transition.ease = easing;
        return this;
    }

    /**
     * Set the duration-milliseconds of the animation.
     * @param {number} duration - The duration in milliseconds. Default is 400ms.
     * @returns {FramerAnimator}
     */
    setDuration(duration) {
        this.duration = duration;
        this.transition.duration = duration / 1000;
        return this;
    }

    /**
     * Set whether the element should be initially hidden.
     * @param {boolean} hide - Whether the element should be hidden initially.. Default is true.
     * @returns {FramerAnimator}
     */
    setInitialHide(hide) {
        this.initialHide = hide;
        return this;
    }

    /**
     * Set initial percentage from where the animation will begin.
     * @param {number} value - Animation-percentage value (from 0 to 100). Default is 50%.
     * @returns {FramerAnimator}
     */
    setInitialPercent(value) {
        this.initialAnimPercent = (value > 100 || value < 0) ? 100 : value;
        return this;
    }

    /**
     * Set repeat-count for animation.
     * @param {number||Infinity} value - Animation-repeat-count value. Default is 1.
     * @returns {FramerAnimator}
     */
    setRepeat(value) {
        this.repeatCount = value == Infinity ? Infinity : parseInteger(value) - 1;
        return this;
    }

    /**
     * Repeat animation infinitely.
     * @returns {FramerAnimator}
     */
    infinite() {
        return this.setRepeat(Infinity);
    }

    /**
     * Set the animation on the loop.
     * @param {boolean} val - Whether the animation should be looped or not. Default is false.
     * @returns {FramerAnimator}
     */
    setLoop(val) {
        this.loop = val;
        return this;
    }

    /**
     * Set the delay before the animation starts.
     * @param {number} delay - The delay in milliseconds. Default is 100ms.
     * @returns {FramerAnimator}
     */
    setDelay(delay) {
        this.delay = delay;
        this.transition.delay = delay / 1000;
        return this;
    }

    /**
     * Set the spring configuration for spring animations.
     * @param {object} config - The spring configuration.
     * @returns {FramerAnimator}
     */
    setSpringConfig(config) {
        this.springConfig = config;
        return this;
    }

    __getCalcInitialMaxValue(maxValue, percentage = this.initialAnimPercent) {
        return (maxValue * percentage) / 100
    }

    /**
     * Get the animation properties based on the type.
     * @param {number} delay The animation delay. Default is "this.delay".
     * @returns {object} - The animation properties.
     */
    __getAnimationProps(delay = this.delay) {
        const opacity = this.initialHide ? 0 : 1;
        const opacityRev = this.initialHide ? 1 : 0;
        const animTransition = {...this.transition, delay: delay / 1000};

        switch (this.animationType) {
            case AnimationType.SLIDE_DOWN:
                return {
                    initial: {y: -this.__getCalcInitialMaxValue(100), opacity: opacity},
                    animate: {y: 0, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.SLIDE_UP:
                return {
                    initial: {y: this.__getCalcInitialMaxValue(100), opacity: opacity},
                    animate: {y: 0, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.SLIDE_LEFT:
                return {
                    initial: {x: this.__getCalcInitialMaxValue(100), opacity: opacity},
                    animate: {x: 0, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.SLIDE_RIGHT:
                return {
                    initial: {x: -this.__getCalcInitialMaxValue(100), opacity: opacity},
                    animate: {x: 0, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.FADE_IN:
                return {
                    initial: {opacity: opacity},
                    animate: {opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.FADE_OUT:
                return {
                    initial: {opacity: opacityRev},
                    animate: {opacity: opacity},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.SCALE_UP:
                return {
                    initial: {scale: opacity, opacity: opacity},
                    animate: {scale: 1, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount, type: 'spring', ...this.springConfig},
                };
            case AnimationType.SCALE_DOWN:
                return {
                    initial: {scale: opacityRev, opacity: opacityRev},
                    animate: {scale: 0, opacity: opacity},
                    transition: {...animTransition, repeat: this.repeatCount, type: 'spring', ...this.springConfig},
                };
            case AnimationType.ROTATE:
                return {
                    initial: {rotate: this.initialHide ? -this.__getCalcInitialMaxValue(180) : 0, opacity: opacity},
                    animate: {rotate: 0, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.ROTATE_IN:
                return {
                    initial: {rotate: this.initialHide ? -this.__getCalcInitialMaxValue(360) : 0, opacity: opacity},
                    animate: {rotate: 0, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.ROTATE_OUT:
                return {
                    initial: {rotate: this.initialHide ? 0 : this.__getCalcInitialMaxValue(360), opacity: opacityRev},
                    animate: {rotate: 360, opacity: opacity},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.BOUNCE:
                return {
                    initial: {y: 0}, animate: {y: [-this.__getCalcInitialMaxValue(30), 0]},
                    transition: {...animTransition, repeat: this.repeatCount, type: 'spring', ...this.springConfig},
                };
            case AnimationType.BOUNCE_IN:
                return {
                    initial: {scale: this.initialHide ? this.__getCalcInitialMaxValue(0.3) : 1, opacity: opacity},
                    animate: {scale: 1, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount, type: 'spring', ...this.springConfig},
                };
            case AnimationType.BOUNCE_OUT:
                return {
                    initial: {scale: this.initialHide ? 1 : this.__getCalcInitialMaxValue(0.3), opacity: opacityRev},
                    animate: {scale: 0.3, opacity: opacity},
                    transition: {...animTransition, repeat: this.repeatCount, type: 'spring', ...this.springConfig},
                };
            case AnimationType.FLIP:
                return {
                    initial: {rotateY: this.initialHide ? 0 : this.__getCalcInitialMaxValue(180), opacity: this.initialHide ? 0 : 1},
                    animate: {rotateY: 180, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.FLIP_IN:
                return {
                    initial: {rotateY: this.initialHide ? -this.__getCalcInitialMaxValue(180) : 0, opacity: opacity},
                    animate: {rotateY: 0, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.FLIP_OUT:
                return {
                    initial: {rotateY: this.initialHide ? 0 : this.__getCalcInitialMaxValue(180), opacity: opacityRev},
                    animate: {rotateY: 180, opacity: opacity},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.ZOOM_IN:
                return {
                    initial: {scale: this.initialHide ? this.__getCalcInitialMaxValue(0.5) : 1, opacity: opacity},
                    animate: {scale: 1, opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.ZOOM_OUT:
                return {
                    initial: {scale: this.initialHide ? 1 : this.__getCalcInitialMaxValue(0.5), opacity: opacityRev},
                    animate: {scale: 0.5, opacity: opacity},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.FLASH:
                return {
                    initial: {opacity: 1},
                    animate: {opacity: [0.5, 1]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.PULSE:
                return {
                    initial: {scale: 1},
                    animate: {scale: [1, 1.05, 1]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.RUBBER_BAND:
                return {
                    initial: {scaleX: 1},
                    animate: {scaleX: [1.25, 1]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.SHAKE:
                return {
                    initial: {x: 0},
                    animate: {x: [-10, 10, -10, 10, 0]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.SWING:
                return {
                    initial: {rotate: 0}, animate: {rotate: [15, -15, 15, -15, 0]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.TADA:
                return {
                    initial: {scale: 1},
                    animate: {scale: [1.05, 1], rotate: [0, -3, 3, -3, 0]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.WOBBLE:
                return {
                    initial: {x: 0},
                    animate: {x: [-5, 5, -5, 5, 0]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.JELLO:
                return {
                    initial: {skewX: 0},
                    animate: {skewX: [10, -10, 10, -10, 0]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            case AnimationType.HEART_BEAT:
                return {
                    initial: {scale: 1},
                    animate: {scale: [1.3, 1]},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
            default:
                return {
                    initial: {opacity: 0},
                    animate: {opacity: 1},
                    transition: {...animTransition, repeat: this.repeatCount},
                };
        }
    }

    __getApplyAnimationProps() {
        switch (this.animationType) {
            case AnimationType.SLIDE_DOWN:
                return {
                    initial: {transform: 'translateY(-100%)', opacity: 0}, animate: {transform: 'translateY(0)', opacity: 1},
                };
            case AnimationType.SLIDE_UP:
                return {
                    initial: {transform: 'translateY(100%)', opacity: 0}, animate: {transform: 'translateY(0)', opacity: 1},
                };
            case AnimationType.SLIDE_LEFT:
                return {
                    initial: {transform: 'translateX(100%)', opacity: 0}, animate: {transform: 'translateX(0)', opacity: 1},
                };
            case AnimationType.SLIDE_RIGHT:
                return {
                    initial: {transform: 'translateX(-100%)', opacity: 0}, animate: {transform: 'translateX(0)', opacity: 1},
                };
            case AnimationType.FADE_IN:
                return {
                    initial: {opacity: 0}, animate: {opacity: 1},
                };
            case AnimationType.FADE_OUT:
                return {
                    initial: {opacity: 1}, animate: {opacity: 0},
                };
            case AnimationType.SCALE_UP:
                return {
                    initial: {transform: 'scale(0)', opacity: 0}, animate: {transform: 'scale(1)', opacity: 1},
                };
            case AnimationType.SCALE_DOWN:
                return {
                    initial: {transform: 'scale(1)', opacity: 1}, animate: {transform: 'scale(0)', opacity: 0},
                };
            case AnimationType.ROTATE:
                return {
                    initial: {transform: 'rotate(0deg)', opacity: 0}, animate: {transform: 'rotate(360deg)', opacity: 1},
                };
            case AnimationType.ROTATE_IN:
                return {
                    initial: {transform: 'rotate(-360deg)', opacity: 0}, animate: {transform: 'rotate(0deg)', opacity: 1},
                };
            case AnimationType.ROTATE_OUT:
                return {
                    initial: {transform: 'rotate(0deg)', opacity: 1}, animate: {transform: 'rotate(360deg)', opacity: 0},
                };
            case AnimationType.BOUNCE:
                return {
                    initial: {transform: 'translateY(0)'}, animate: {transform: 'translateY(-30px)'},
                };
            case AnimationType.BOUNCE_IN:
                return {
                    initial: {transform: 'scale(0.3)', opacity: 0}, animate: {transform: 'scale(1)', opacity: 1},
                };
            case AnimationType.BOUNCE_OUT:
                return {
                    initial: {transform: 'scale(1)', opacity: 1}, animate: {transform: 'scale(0.3)', opacity: 0},
                };
            case AnimationType.FLIP:
                return {
                    initial: {transform: 'rotateY(0deg)', opacity: 0}, animate: {transform: 'rotateY(180deg)', opacity: 1},
                };
            case AnimationType.FLIP_IN:
                return {
                    initial: {transform: 'rotateY(-180deg)', opacity: 0}, animate: {transform: 'rotateY(0deg)', opacity: 1},
                };
            case AnimationType.FLIP_OUT:
                return {
                    initial: {transform: 'rotateY(0deg)', opacity: 1}, animate: {transform: 'rotateY(180deg)', opacity: 0},
                };
            case AnimationType.ZOOM_IN:
                return {
                    initial: {transform: 'scale(0.5)', opacity: 0}, animate: {transform: 'scale(1)', opacity: 1},
                };
            case AnimationType.ZOOM_OUT:
                return {
                    initial: {transform: 'scale(1)', opacity: 1}, animate: {transform: 'scale(0.5)', opacity: 0},
                };
            case AnimationType.FLASH:
                return {
                    initial: {opacity: 1}, animate: {opacity: 0.5},
                };
            case AnimationType.PULSE:
                return {
                    initial: {transform: 'scale(1)'}, animate: {transform: 'scale(1.05)'},
                };
            case AnimationType.RUBBER_BAND:
                return {
                    initial: {transform: 'scaleX(1)'}, animate: {transform: 'scaleX(1.25)'},
                };
            case AnimationType.SHAKE:
                return {
                    initial: {transform: 'translateX(0)'}, animate: {transform: 'translateX(-10px)'},
                };
            case AnimationType.SWING:
                return {
                    initial: {transform: 'rotate(0deg)'}, animate: {transform: 'rotate(15deg)'},
                };
            case AnimationType.TADA:
                return {
                    initial: {transform: 'scale(1)'}, animate: {transform: 'scale(1.05)'},
                };
            case AnimationType.WOBBLE:
                return {
                    initial: {transform: 'translateX(0)'}, animate: {transform: 'translateX(-5px)'},
                };
            case AnimationType.JELLO:
                return {
                    initial: {transform: 'skewX(0deg)'}, animate: {transform: 'skewX(10deg)'},
                };
            case AnimationType.HEART_BEAT:
                return {
                    initial: {transform: 'scale(1)'}, animate: {transform: 'scale(1.3)'},
                };
            default:
                return {
                    initial: {opacity: 0}, animate: {opacity: 1},
                };
        }
    }

    /**
     * Render the animation component.
     * @param {JSX.Element} children Children to render.
     * @param {Object} wrapperProps={} An object containing properties/attributes (i.e. className, style etc.) for the wrapper-element.
     * @returns {JSX.Element}
     */
    render(children, wrapperProps = {}) {
        return (<motion.div
            {...wrapperProps}
            {...this.__getAnimationProps()}>
            {children}
        </motion.div>);

    }

    /**
     * Renders each-child of the element in waterfall-like sequence animation
     * with progressive delays & specified {@link AnimationType}.
     * @param {JSX.Element} element Parent element containing children.
     * @param {Object} wrapperProps An object containing properties/attributes (i.e. className, style etc.) for the wrapper-element.
     * @returns {JSX.Element}
     */
    renderChildren(element, wrapperProps = {}) {
        return elementHasChildren(element)
            && <
                //  && <div {...wrapperProps}
            >
                {React.Children.toArray(element.props.children).map((child, index) => {
                    return <motion.div
                        key={index}
                        {...this.__getAnimationProps(this.delay * (index))}
                    >
                        {child}
                    </motion.div>
                })}
            </>
        // </div>
    }

    _getRenderElIdAttrs(el) {
        const isElHtml = isHtmlElement(el);
        const isElReact = isReactElement(el);
        if (!isElHtml && !isElReact) return {};
        const noIdFound = !checkNullStr(this.elementClassName) && !checkNullStr(this.elementId) && (!checkNull(this.elementRef) || (checkNull(this.elementRef) && !checkNull(this.elementRef.current)));
        // this._log('_getRenderElIdAttrs.el:', el);
        const elCls = noIdFound ? (isElHtml ? getDefStr(el.className) : (isElReact ? el.props.className : '')) : this.elementClassName;
        const elId = noIdFound ? (isElHtml ? getDefStr(el.id) : (isElReact ? getDefStr(el.props.id) : '')) : this.elementId;
        const elRef = noIdFound ? (isElHtml ? ((checkNull(el.getAttribute('ref'))) ? {ref: el.getAttribute('ref')} : {}) : isElReact ? ((checkNull(el.props.ref)) ? {ref: el.props.ref.current} : {}) : {}) : ((this.elementRef && this.elementRef.current) ? {ref: this.elementRef} : {});
        // this._log('_getRenderElIdAttrs.el.noIdFound:', noIdFound, 'isElHtml:', isElHtml, 'isElReact:', isElReact, 'elId:', elId, 'elCls:', elCls, 'elRef:', elRef);
        return {
            ...checkNullStr(elCls) ? {className: elCls} : {}, ...checkNullStr(elId) ? {id: elId} : {}, ...(checkNull(elRef) && checkNullJson(elRef)) ? {ref: elRef} : {},
        }
    }

    applyAnimation() {
        if (checkNullStr(this.elementId) || (this.elementRef && this.elementRef.current)) {
            const element = this.elementRef.current;
            const animation = this.__getApplyAnimationProps();
            element.style.transition = `all ${this.duration}ms ${this.easing}`;
            Object.assign(element.style, animation.initial);

            setTimeout(() => {
                Object.assign(element.style, animation.animate);
            }, this.delay);
        }
    }

    _log(...text) {
        printLog(this.TAG, ...text);
    }

    _logErr(...text) {
        printError(this.TAG, ...text);
    }
}

export {FramerAnimator, AnimationType, EasingType};
